/** Small, dependency-free input checks for the auth/onboarding forms (unit-tested). */
import { looksLikeEmail, normalizeEmail } from '../../../shared/email';

export const NAME_MAX = 100;

/** Same whitespace the server collapses (incl. no-break / thin / zero-width spaces). */
const PHONE_SPACES = /[\s  -​ ]+/g;

/**
 * Mirrors the server's phone rule (PATCH /api/profile normalizePhone): optional leading +, then a
 * digit or "(", then only digits, spaces, ( ) - . — at most 40 characters — plus 7–15 digits.
 */
export function phoneOk(v: string): boolean {
  const s = v.replace(PHONE_SPACES, ' ').trim();
  if (!s) return true;
  if (s.length > 40 || !/^\+?[0-9(][0-9 ()\-.]*$/.test(s)) return false;
  const digits = s.replace(/\D/g, '').length;
  return digits >= 7 && digits <= 15;
}

/** Empty (optional field) or a plausible e-mail address. */
export function emailOk(v: string): boolean {
  const s = v.trim();
  return !s || (looksLikeEmail(s) && s.length <= 254);
}

/**
 * Top-level field names a 422 'validation' response rejected. The server sends
 * details = [{ path: ['phone'], … }] (own checks) or zod issues with the same `path` shape.
 */
export function rejectedFields(details: unknown): Set<string> {
  const out = new Set<string>();
  if (!Array.isArray(details)) return out;
  for (const d of details) {
    const path = d && typeof d === 'object' ? (d as { path?: unknown }).path : undefined;
    if (Array.isArray(path) && typeof path[0] === 'string') out.add(path[0]);
  }
  return out;
}

export interface SignupFields {
  fullName: string;
  email: string;
  phone: string;
}
export type SignupInvalid = Partial<Record<keyof SignupFields, true>>;

/**
 * Sign-up fields of an invite link by voice that the server would refuse: a name is required, the login
 * e-mail must look valid (≤ 254 characters after normalising), the phone is optional (phoneOk).
 */
export function signupInvalid(f: SignupFields): SignupInvalid {
  const out: SignupInvalid = {};
  if (!f.fullName.trim()) out.fullName = true;
  const email = normalizeEmail(f.email);
  if (!looksLikeEmail(email) || email.length > 254) out.email = true;
  if (!phoneOk(f.phone)) out.phone = true;
  return out;
}
